import { useState } from "react";
import { toast } from "sonner";
import { Check, Network, ScrollText, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { OrgNode } from "@/views/exec/OrgNode";

const eur = (v) => `€${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tone = (a) => (a >= 100 ? "text-emerald-700" : a >= 70 ? "text-amber-700" : "text-red-700");

export function OrgChart() {
  const q = useApi("/exec/org-chart");
  const d = q.data;
  if (!d) return null;
  return (
    <div className="border rounded-lg bg-card p-3 mb-4" data-testid="exec-org-chart">
      <div className="eyebrow flex items-center gap-1 mb-1">
        <Network className="h-3 w-3" />Org chart
        <span className="ml-auto normal-case tracking-normal font-sans text-xs text-muted-foreground">{d.headcount} people · {d.teams} teams</span>
      </div>
      {d.root ? <OrgNode n={d.root} depth={0} /> : <div className="text-xs text-muted-foreground">No leadership team yet.</div>}
    </div>
  );
}

function ApprovalRow({ r, onDone }) {
  const [reason, setReason] = useState("");
  const act = async (action) => {
    try {
      const { data } = await api.post(`/finance/${r.model}/${r.id}/${action}`, { reason: reason || null });
      toast.success(action === "approve" ? `${r.ref} approved — ${data.state}` : `${r.ref} rejected`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <li data-testid={`ceo-approval-${r.id}`} className="px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
      <span className="font-mono font-medium">{r.ref}</span>
      <span>{r.partner}</span>
      <b>{eur(r.total)}</b>
      <span className="text-muted-foreground">{r.company} · CFO {r.cfo}{r.comment ? ` — “${r.comment}”` : ""}</span>
      <span className="ml-auto flex items-center gap-1">
        <Input data-testid={`ceo-approval-reason-${r.id}`} className="h-7 w-40 text-xs" placeholder="comment / reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        <Button size="sm" className="h-7 bg-emerald-700 hover:bg-emerald-800" data-testid={`ceo-approve-${r.id}`} onClick={() => act("approve")}><Check className="h-3 w-3 mr-1" />Approve</Button>
        <Button size="sm" variant="outline" className="h-7 border-red-300 text-red-700" data-testid={`ceo-reject-${r.id}`} disabled={!reason.trim()} onClick={() => act("reject")}><X className="h-3 w-3 mr-1" />Reject</Button>
      </span>
    </li>
  );
}

// Documents above the CEO threshold waiting for one-tap sign-off.
export function CeoApprovals() {
  const q = useApi("/exec/ceo/approvals");
  const rows = q.data || [];
  return (
    <div className="border rounded-lg bg-card mb-4" data-testid="exec-ceo-approvals">
      <div className="px-3 py-2 eyebrow flex items-center gap-1"><ScrollText className="h-3 w-3" />Awaiting your sign-off <span className="text-muted-foreground">({rows.length})</span></div>
      <ul className="divide-y">{rows.map((r) => <ApprovalRow key={r.id} r={r} onDone={q.reload} />)}</ul>
      {rows.length === 0 ? <div className="px-3 py-3 text-xs text-muted-foreground">Nothing waiting. Documents above the CEO threshold (Finance → Alerts & nudges) land here after CFO approval.</div> : null}
    </div>
  );
}

export function CeoBriefButton({ s }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/exec/ceo/brief");
      toast.success(`Brief posted to #${data.channel}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return <Button size="sm" variant="outline" className="h-7" data-testid="exec-ceo-brief-now" disabled={busy || !s.brief_channel_id} onClick={go}><Send className="h-3 w-3 mr-1" />Post weekly brief now</Button>;
}

// Inline monthly target editor for a team row (department heads only).
export function GoalCell({ dept, team, month, onSaved }) {
  const [v, setV] = useState(team.target == null ? "" : String(team.target));
  const save = async () => {
    const t = v === "" ? null : Number(v);
    const current = team.target == null ? null : team.target;
    if (t === current) return;
    try {
      await api.put(`/exec/${dept}/goals`, { team_id: team.team_id, month, target: t });
      toast.success(t == null ? "Target cleared" : "Target saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <span className="flex items-center gap-1 justify-end">
      <Input type="number" data-testid={`exec-goal-${team.team_id}`} className="h-7 w-24 text-xs font-mono text-right" placeholder="target" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} />
      {team.attainment != null ? <span data-testid={`exec-attain-${team.team_id}`} className={`text-[11px] font-mono ${tone(team.attainment)}`}>{team.attainment}%</span> : null}
    </span>
  );
}
